import { useTranslation } from "react-i18next";

// ─── Helpers ───────────────────────────────────────────────────

function joinNames(names, t) {
  const cleaned = names.filter(Boolean);
  if (cleaned.length === 0) return "";
  if (cleaned.length === 1) return cleaned[0];
  if (cleaned.length === 2)
    return t("rsvp.recap.joinTwo", { first: cleaned[0], second: cleaned[1] });
  const finalSep = t("rsvp.recap.joinFinal");
  return `${cleaned.slice(0, -1).join(", ")}${finalSep}${cleaned[cleaned.length - 1]}`;
}

// "You" personalization — matches the guest's own name to soften the
// recap. Kept as-is for now; we always render their full name for
// simplicity and consistency with the rest of the flow.
function firstName(member) {
  return (member?.name || "").split(" ")[0] || member?.name || "";
}

// Bucket members for a single section based on their responses.
function bucketSection(section, members, responses) {
  const buckets = { attending: [], notAttending: [], maybe: [], pending: [] };
  for (const member of members) {
    const r = responses.find(
      (x) => x.memberId === member._id && x.sectionId === section._id,
    );
    const status = r?.status || "pending";
    if (buckets[status]) {
      buckets[status].push(member);
    } else {
      buckets.pending.push(member);
    }
  }
  return buckets;
}

// Compose the sentence(s) for a single section from its buckets.
function sectionSentence(section, members, buckets, t) {
  const totalMembers = members.length;
  const sentences = [];
  const singleMember = totalMembers === 1;
  const yes = buckets.attending;
  const no = buckets.notAttending;
  const maybe = buckets.maybe;

  // Lead clause — describes who's coming (or if nobody is).
  if (yes.length === totalMembers && totalMembers > 0) {
    if (singleMember) {
      sentences.push(t("rsvp.recap.allJoiningSolo"));
    } else {
      sentences.push(
        t("rsvp.recap.allJoiningGroup", {
          count: totalMembers,
          names: joinNames(yes.map(firstName), t),
        }),
      );
    }
  } else if (yes.length > 0) {
    if (yes.length === 1) {
      sentences.push(t("rsvp.recap.someYesSingle", { name: firstName(yes[0]) }));
    } else {
      sentences.push(
        t("rsvp.recap.someYesMulti", { names: joinNames(yes.map(firstName), t) }),
      );
    }
  } else if (no.length > 0 && maybe.length === 0) {
    // Everyone either declined or didn't answer; no yeses.
    if (singleMember) {
      sentences.push(t("rsvp.recap.allDeclinedSolo"));
    } else if (no.length === totalMembers) {
      sentences.push(t("rsvp.recap.allDeclinedGroup"));
    }
  }

  // Decliners — only mentioned when some yeses exist.
  if (no.length > 0 && yes.length > 0) {
    if (no.length === 1) {
      sentences.push(t("rsvp.recap.declinedSingle", { name: firstName(no[0]) }));
    } else {
      sentences.push(
        t("rsvp.recap.declinedMulti", { names: joinNames(no.map(firstName), t) }),
      );
    }
  }

  // Maybe clause.
  if (maybe.length > 0) {
    if (maybe.length === 1) {
      sentences.push(t("rsvp.recap.maybeSingle", { name: firstName(maybe[0]) }));
    } else {
      sentences.push(
        t("rsvp.recap.maybeMulti", { names: joinNames(maybe.map(firstName), t) }),
      );
    }
  }

  // Safety net — if we somehow produced nothing (all pending), say so.
  if (sentences.length === 0) {
    sentences.push(
      singleMember
        ? t("rsvp.recap.pendingSolo")
        : t("rsvp.recap.pendingMulti"),
    );
  }

  return sentences;
}

// Aggregate tone for the headline: "celebrate" when anyone is attending
// anywhere; otherwise a thankful, respectful line.
function pickHeadline(sections, members, responses, t) {
  const anyYes = sections.some((section) => {
    const buckets = bucketSection(section, members, responses);
    return buckets.attending.length > 0;
  });
  if (anyYes) return t("rsvp.recap.celebrateHeadline");
  return t("rsvp.recap.thankYouHeadline");
}

// ─── Component ─────────────────────────────────────────────────

/**
 * RsvpRecap
 *
 * Renders a warm, sentence-driven recap of a guest's submitted RSVP.
 *
 * Props:
 *   sections  — EventSection[] (with _id and name)
 *   members   — { _id, name }[]
 *   responses — { memberId, sectionId, status }[]
 *   closing   — optional node shown under the recap
 */
const RsvpRecap = ({
  sections = [],
  members = [],
  responses = [],
  closing = null,
}) => {
  const { t } = useTranslation();
  const headline = pickHeadline(sections, members, responses, t);
  const hasContent = sections.length > 0 && members.length > 0;
  const atPrefix = t("rsvp.recap.atPrefix");
  const atSuffix = t("rsvp.recap.atSuffix");

  return (
    <div style={{ textAlign: "center", padding: "16px 0 8px" }}>
      <h2
        id="rsvp-modal-title"
        className="heading-60"
        style={{ margin: 0, marginBottom: 28 }}
      >
        {headline}
      </h2>

      {hasContent && (
        <div
          style={{
            textAlign: "left",
            maxWidth: 500,
            margin: "0 auto 24px",
          }}
        >
          {sections.map((section, idx) => {
            const buckets = bucketSection(section, members, responses);
            const sentences = sectionSentence(section, members, buckets, t);
            return (
              <p
                key={section._id}
                className="body-16"
                style={{
                  margin: 0,
                  padding: "14px 0",
                  borderTop:
                    idx === 0 ? "none" : "1px solid rgba(0,0,0,0.08)",
                  lineHeight: 1.7,
                }}
              >
                {atPrefix}
                <strong>{section.name}</strong>
                {atSuffix}
                {sentences.join(" ")}
              </p>
            );
          })}
        </div>
      )}

      <p
        className="body-16"
        style={{ opacity: 0.55, margin: 0, maxWidth: 420, marginLeft: "auto", marginRight: "auto" }}
      >
        {closing || t("rsvp.recap.closing")}
      </p>
    </div>
  );
};

export default RsvpRecap;
